import React, { useState, useMemo } from "react";
import { api, fmtMoney, formatErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Plus, X, Package, PenLine, Truck, FileText } from "lucide-react";
import { toast } from "sonner";

/**
 * "New order" dialog for admins. Supports two routes:
 *   - Save as ORDER → places it immediately + emails the customer payment request
 *   - Save as QUOTE → draft only (admin sends via the row's Send action)
 *
 * Line items can be picked from the catalog OR typed in free-form (description + price + qty).
 */
export default function OrderCreateDialog({ customers, products, onCreated }) {
  const [open, setOpen] = useState(false);
  const [customerId, setCustomerId] = useState("");
  const [notes, setNotes] = useState("");
  const [items, setItems] = useState([]); // [{product_id?, description, quantity, unit_price, taxable}]
  const [picker, setPicker] = useState({ kind: "catalog", product_id: "", quantity: 1, description: "", unit_price: 0, taxable: true });
  const [fulfillment, setFulfillment] = useState("ticket"); // default to delivery — most product orders ship
  const [deliveryDate, setDeliveryDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [busy, setBusy] = useState(false);

  const customer = useMemo(() => customers.find((c) => c.id === customerId), [customers, customerId]);
  const subtotal = items.reduce((s, it) => s + Number(it.unit_price) * Number(it.quantity), 0);

  const reset = () => {
    setCustomerId(""); setNotes(""); setItems([]);
    setPicker({ kind: "catalog", product_id: "", quantity: 1, description: "", unit_price: 0, taxable: true });
    setFulfillment("invoice");
    setDeliveryDate(new Date().toISOString().slice(0, 10));
  };

  const addLine = () => {
    if (picker.kind === "catalog") {
      const p = products.find((x) => x.id === picker.product_id);
      if (!p) return toast.error("Pick a product first");
      if (!picker.quantity || picker.quantity <= 0) return toast.error("Quantity must be > 0");
      setItems([...items, {
        product_id: p.id, description: p.name,
        quantity: Number(picker.quantity), unit_price: Number(p.price), taxable: p.taxable !== false,
      }]);
    } else {
      if (!picker.description.trim()) return toast.error("Add a description");
      if (!picker.quantity || picker.quantity <= 0) return toast.error("Quantity must be > 0");
      setItems([...items, {
        product_id: null, description: picker.description.trim(),
        quantity: Number(picker.quantity), unit_price: Number(picker.unit_price || 0), taxable: picker.taxable,
      }]);
    }
    setPicker({ kind: picker.kind, product_id: "", quantity: 1, description: "", unit_price: 0, taxable: true });
  };

  const save = async (saveAs) => {
    if (!customerId) return toast.error("Pick a customer");
    if (items.length === 0) return toast.error("Add at least one line item");
    if (fulfillment === "ticket" && !deliveryDate) return toast.error("Pick a delivery date");
    setBusy(true);
    try {
      await api.post("/orders", {
        customer_id: customerId,
        notes,
        items: items.map((it) => ({
          product_id: it.product_id || null,
          description: it.description,
          quantity: Number(it.quantity),
          unit_price: Number(it.unit_price),
          taxable: !!it.taxable,
        })),
        save_as: saveAs,
        fulfillment_method: fulfillment,
        delivery_date: fulfillment === "ticket" ? deliveryDate : null,
      });
      toast.success(saveAs === "quote" ? "Quote saved as draft" : "Order placed — customer emailed");
      setOpen(false); reset();
      onCreated && onCreated();
    } catch (e) { toast.error(formatErr(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) reset(); }}>
      <DialogTrigger asChild>
        <Button className="rounded-sm h-10" data-testid="new-order-button">
          <Plus className="w-4 h-4 mr-2" /> New order / quote
        </Button>
      </DialogTrigger>
      <DialogContent className="rounded-sm max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display">New order or quote</DialogTitle>
          <DialogDescription>
            Add catalog products or custom line items. Save as a <strong>quote</strong> to send for customer approval, or as an <strong>order</strong> to place it immediately (customer is emailed a payment request).
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <Label>Customer</Label>
            <Select value={customerId} onValueChange={setCustomerId}>
              <SelectTrigger className="rounded-sm" data-testid="order-customer-select"><SelectValue placeholder="Select customer" /></SelectTrigger>
              <SelectContent>
                {customers.map((c) => (
                  <SelectItem key={c.id} value={c.id}>{c.business_name || c.name}{c.business_name && c.name ? ` — ${c.name}` : ""}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {customer?.tax_exempt && (
              <p className="text-xs text-success mt-1">Tax exempt — order tax will be $0.</p>
            )}
          </div>

          {/* Line items */}
          <div className="border border-border p-4 space-y-3">
            <div className="flex items-center justify-between">
              <p className="overline">Line items</p>
              <div className="flex gap-1">
                <Button type="button" size="sm" variant={picker.kind === "catalog" ? "default" : "outline"} className="rounded-sm h-7 text-xs"
                        onClick={() => setPicker({ ...picker, kind: "catalog" })} data-testid="order-picker-catalog">
                  <Package className="w-3 h-3 mr-1" /> Catalog
                </Button>
                <Button type="button" size="sm" variant={picker.kind === "custom" ? "default" : "outline"} className="rounded-sm h-7 text-xs"
                        onClick={() => setPicker({ ...picker, kind: "custom" })} data-testid="order-picker-custom">
                  <PenLine className="w-3 h-3 mr-1" /> Custom
                </Button>
              </div>
            </div>

            {picker.kind === "catalog" ? (
              <div className="grid grid-cols-12 gap-2 items-end">
                <div className="col-span-7">
                  <Label className="text-xs">Product</Label>
                  <Select value={picker.product_id} onValueChange={(v) => setPicker({ ...picker, product_id: v })}>
                    <SelectTrigger className="rounded-sm" data-testid="order-product-select">
                      <SelectValue placeholder={products.length ? "Select product" : "No active products"} />
                    </SelectTrigger>
                    <SelectContent>{products.map((p) => <SelectItem key={p.id} value={p.id}>{p.name} — {fmtMoney(p.price)}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="col-span-3">
                  <Label className="text-xs">Qty</Label>
                  <Input type="number" min="1" step="1" className="rounded-sm mono-num"
                         value={picker.quantity}
                         onChange={(e) => setPicker({ ...picker, quantity: parseInt(e.target.value || 1) })}
                         data-testid="order-product-qty" />
                </div>
                <div className="col-span-2">
                  <Button type="button" onClick={addLine} variant="outline" className="rounded-sm w-full" data-testid="order-add-catalog">
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-12 gap-2 items-end">
                <div className="col-span-5">
                  <Label className="text-xs">Description</Label>
                  <Input className="rounded-sm" value={picker.description} placeholder="e.g. Filter cleaning service"
                         onChange={(e) => setPicker({ ...picker, description: e.target.value })}
                         data-testid="order-custom-desc" />
                </div>
                <div className="col-span-2">
                  <Label className="text-xs">Qty</Label>
                  <Input type="number" min="1" step="1" className="rounded-sm mono-num" value={picker.quantity}
                         onChange={(e) => setPicker({ ...picker, quantity: parseInt(e.target.value || 1) })}
                         data-testid="order-custom-qty" />
                </div>
                <div className="col-span-3">
                  <Label className="text-xs">Unit price</Label>
                  <Input type="number" min="0" step="0.01" className="rounded-sm mono-num" value={picker.unit_price}
                         onChange={(e) => setPicker({ ...picker, unit_price: parseFloat(e.target.value || 0) })}
                         data-testid="order-custom-price" />
                </div>
                <div className="col-span-2">
                  <Button type="button" onClick={addLine} variant="outline" className="rounded-sm w-full" data-testid="order-add-custom">
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            )}

            {items.length > 0 && (
              <div className="border-t border-border pt-2 space-y-1" data-testid="order-items-list">
                {items.map((it, i) => (
                  <div key={`${i}-${it.description}`} className="flex items-center justify-between text-sm py-1.5">
                    <span className="flex-1">{it.description}{!it.taxable && <span className="text-xs text-muted-foreground ml-2">(non-tax)</span>}</span>
                    <span className="mono-num w-16 text-right">{it.quantity}×</span>
                    <span className="mono-num w-24 text-right">{fmtMoney(it.unit_price)}</span>
                    <span className="mono-num w-24 text-right font-semibold">{fmtMoney(it.unit_price * it.quantity)}</span>
                    <Button size="sm" variant="ghost" className="rounded-sm ml-2 h-7 w-7 p-0"
                            onClick={() => setItems(items.filter((_, idx) => idx !== i))}
                            data-testid={`order-remove-line-${i}`}>
                      <X className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div>
            <Label>Notes (optional)</Label>
            <Textarea className="rounded-sm" rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="order-notes" />
          </div>

          {/* Pre-select where this order goes when payment is recorded. */}
          <div className="border border-primary/30 bg-primary/5 rounded-sm p-4 space-y-2">
            <p className="overline text-primary">When payment is received, this order goes to:</p>
            <RadioGroup value={fulfillment} onValueChange={setFulfillment} className="grid sm:grid-cols-2 gap-2">
              <label className="flex items-start gap-3 cursor-pointer border border-border bg-background rounded-sm p-3 hover:border-primary/50">
                <RadioGroupItem value="invoice" id="fm-invoice" data-testid="fulfillment-invoice" />
                <div className="flex-1">
                  <div className="font-medium text-sm flex items-center gap-1"><FileText className="w-3.5 h-3.5" /> Pickup / no visit</div>
                  <div className="text-xs text-muted-foreground">Auto-creates a paid invoice — customer picks up.</div>
                </div>
              </label>
              <label className="flex items-start gap-3 cursor-pointer border border-border bg-background rounded-sm p-3 hover:border-primary/50">
                <RadioGroupItem value="ticket" id="fm-ticket" data-testid="fulfillment-ticket" />
                <div className="flex-1">
                  <div className="font-medium text-sm flex items-center gap-1"><Truck className="w-3.5 h-3.5" /> Scheduled delivery</div>
                  <div className="text-xs text-muted-foreground">Auto-creates an open ticket — driver delivers it.</div>
                </div>
              </label>
            </RadioGroup>
            {fulfillment === "ticket" && (
              <div className="mt-2 flex items-center gap-3 border-t border-primary/20 pt-3">
                <Label className="text-xs font-semibold whitespace-nowrap">Delivery date</Label>
                <Input type="date" className="rounded-sm h-9 max-w-[200px]"
                       value={deliveryDate}
                       onChange={(e) => setDeliveryDate(e.target.value)}
                       data-testid="create-delivery-date" />
                <span className="text-xs text-muted-foreground">— driver sees this on Driver Day</span>
              </div>
            )}
          </div>

          <div className="flex justify-between items-center text-sm pt-2 border-t border-border">
            <span className="overline">Subtotal preview</span>
            <span className="font-display text-2xl font-black tracking-tighter mono-num" data-testid="order-subtotal-preview">
              {fmtMoney(subtotal)}
            </span>
          </div>
        </div>

        <DialogFooter className="gap-2 flex-col sm:flex-row">
          <Button variant="outline" onClick={() => setOpen(false)} className="rounded-sm">Cancel</Button>
          <Button variant="outline" onClick={() => save("quote")} disabled={busy} className="rounded-sm" data-testid="save-as-quote-button">
            Save as quote
          </Button>
          <Button onClick={() => save("order")} disabled={busy} className="rounded-sm" data-testid="save-as-order-button">
            Place order &amp; email customer
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
